/**
 * UCC Paper Filings Hooks
 *
 * React Query hooks for paper filing submission pipeline operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { uccPaperFilingClient } from '../api/typed-clients';
import { retryUnlessClientError, unwrapUccResponse } from './ucc-response';

const PAPER_FILINGS_KEY = 'uccPaperFilings';

export function usePaperFilings(params: { tenantId: string; status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [PAPER_FILINGS_KEY, params],
    queryFn: async () => {
      const result = await uccPaperFilingClient.GET('/ucc/paper-filings', {
        params: {
          query: {
            tenantId: params.tenantId,
            status: params.status as never,
            page: params.page,
            limit: params.limit,
          },
        },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
    // A 404 means the service is not deployed; say so on the first answer.
    retry: retryUnlessClientError,
  });
}

export function usePaperFiling(filingId: string | undefined) {
  return useQuery({
    queryKey: [PAPER_FILINGS_KEY, filingId],
    queryFn: async () => {
      const result = await uccPaperFilingClient.GET('/ucc/paper-filings/{filingId}', {
        params: { path: { filingId: filingId! } },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!filingId,
  });
}

export function useCreatePaperFiling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: never) => {
      const result = await uccPaperFilingClient.POST('/ucc/paper-filings', { body });
      return unwrapUccResponse(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAPER_FILINGS_KEY] });
    },
  });
}

export function useUpdateTracking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ filingId, body }: { filingId: string; body: never }) => {
      const result = await uccPaperFilingClient.PATCH('/ucc/paper-filings/{filingId}/tracking', {
        params: { path: { filingId } },
        body,
      });
      return unwrapUccResponse(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAPER_FILINGS_KEY] });
    },
  });
}

export function useCaptureAcknowledgment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ filingId, body }: { filingId: string; body: never }) => {
      const result = await uccPaperFilingClient.PATCH('/ucc/paper-filings/{filingId}/acknowledgment', {
        params: { path: { filingId } },
        body,
      });
      return unwrapUccResponse(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAPER_FILINGS_KEY] });
    },
  });
}
